import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { EquipmentLogService } from 'src/shared/services/equipment-log.service';
import { jsPDF } from 'jspdf';
import { EquipmentLog, PaginationInfo } from './equipment-log-assets/equipment-log-model';
import { ErrorLibService } from 'src/shared/services/error-lib.service';
import Swal from 'sweetalert2';
import { Unsubscriber } from 'src/shared/components/unsubscriber/unsubscriber.component';
import { tzConvertISOEndDate, tzConvertISOStartDate, tzConvertUTC2Local } from 'src/assets/convertTimeZone/convertTimeZone';

// Imports Standalone
import { PageHeaderComponent } from 'src/shared/components/page-header/page-header.component';
import { TableHeaderComponent } from 'src/shared/helper-components/table-header/table-header.component';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { LogsFormComponent } from '../logs-form/logs-form.component';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatInputModule } from '@angular/material/input';
import { DatePickerModule } from 'primeng/datepicker';

@Component({
  selector: 'app-equipment-log',
  templateUrl: './equipment-log.component.html',
  styleUrls: ['./equipment-log.component.less'],
  imports: [
    CommonModule,
    PageHeaderComponent,
    TableHeaderComponent,
    LogsFormComponent,
    ReactiveFormsModule,
    TableModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatProgressBarModule,
    TranslateModule,
    DatePickerModule
  ]
})

export class EquipmentLogComponent extends Unsubscriber implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private equipmentLogService: EquipmentLogService,
    private errorLibService: ErrorLibService
  ) {
    super();
  }

  ngOnInit() { }

  // Armazena o ID da empresa selecionada (Pode iniciar com o valor armazenado localStorage ou null)
  public companyId: string | null = localStorage.getItem('lastCompanySelected') ? localStorage.getItem('lastCompanySelected') : null;

  // Variaveis responsáveis por abrir e fechar as boxes
  public filterBox: boolean = false;
  public paginationProperties: PaginationInfo = new PaginationInfo(0, 0, false, false, null, null);

  // Variaveis responsáveis pelos loadings da tela
  public filterLoading: boolean = false;
  public fileLoading: boolean = false;

  // Função responsável por mudar o estado da box de filtro
  filterBoxOpen(): void {
    this.filterBox = !this.filterBox;
  }

  /** Variáveis utilizadas para armazenar o filtro do usuário **/
  public operation: string;
  public startDatetime: any;
  public endDatetime: any;
  public serialNumber: string;
  public macAddress: string;
  public username: string;

  //Variavel responsável por armazenas os dados da tabela
  public ELEMENT_DATA: EquipmentLog[] = [];

  // Renderiza os comandos da lista ELEMENT_DATA para a tabela
  public dataSource = new MatTableDataSource<EquipmentLog>(this.ELEMENT_DATA);

  /** Variáveis utilizadas para manipulação do Paginator **/
  @ViewChild(MatPaginator) paginator: MatPaginator;
  public currentPage: number = 0;
  public pageIndex: number = 0;
  public pageSize: number = 10;
  public length: number = 0;
  public previousPageIndex: number = 0;

  /** Função chamada toda vez que ocorre um evento no paginator **/
  public pageChanged(event: any) {

    /** Armazena o indice da página atual **/
    const newPageIndex = event.first;

    /** Atualiza o valor da página anterior para a página atual **/
    this.previousPageIndex = this.pageIndex;

    /** Atualiza o índice da página atual **/
    this.pageIndex = newPageIndex;
    /** 
     * Caso o botão pressionado seja o de página anterior utiliza o
     * startCursor como cursor para o novo filtro
     * 
     * Caso o botão pressionado seja o de próxima página é utilizado
     * o endCursor como cursor
     **/
    if (this.previousPageIndex > newPageIndex) {
      this.filterEquipmentLogs(this.paginationProperties.startCursor)
    } else if (this.previousPageIndex < newPageIndex) {
      this.filterEquipmentLogs(this.paginationProperties.endCursor)
    }
  }

  /** Variáveis utilizadas para geração e manipulação dos arquivos **/
  public FILE_DATA: EquipmentLog[] = [];
  public isReady: boolean = false;

  /**
   * Função que configura os dados da tabela.
   */
  public setDataSourceAttributes() {
    this.dataSource.data = this.ELEMENT_DATA;
  }

  // Armazena os dados do formulário de filtro de logs, o valor é setado automáticamente pelo HTML
  public filterLogsForm: FormGroup = this.formBuilder.group({
    operation: null,
    serialNumber: null,
    macAddress: null,
    username: null,
    logsDateTime: null
  });

  /**
   * Função responsavel por filtrar logs
   */
  public filterEquipmentLogs(cursor: string | null) {

    this.filterLoading = true;

    this.operation = this.filterLogsForm.get('operation')?.value || null;

    this.startDatetime = this.filterLogsForm?.value.logsDateTime?.[0]
      ? tzConvertISOStartDate(this.filterLogsForm?.value.logsDateTime?.[0])
      : null;

    this.endDatetime = this.filterLogsForm?.value?.logsDateTime?.[1]
      ? tzConvertISOEndDate(this.filterLogsForm?.value?.logsDateTime?.[1])
      : null;

    this.serialNumber = this.filterLogsForm.get('serialNumber')?.value || null;

    this.macAddress = this.filterLogsForm.get('macAddress')?.value || null;

    this.username = this.filterLogsForm.get('username')?.value || null;

    if (cursor === null) {
      this.pageIndex = 0;
      this.isReady = true;
    }

    this.subscriptions = this.equipmentLogService.filterLogs(
      this.companyId,
      this.operation,
      this.startDatetime,
      this.endDatetime,
      this.serialNumber,
      this.macAddress,
      this.username,
      cursor,
      this.pageSize
    ).valueChanges.subscribe({
      next: ((response: any) => {
        // Validando se a resposta da API está carregando ou se os dados estão disponíveis para evitar erros de renderização
        if (response.loading || !response.data) {
          return;
        }

        this.paginationProperties = new PaginationInfo(
          response?.data?.equipmentLog?.count,
          response?.data?.equipmentLog?.total,
          response?.data?.equipmentLog?.pageInfo?.hasNextPage,
          response?.data?.equipmentLog?.pageInfo?.hasPreviousPage,
          response?.data?.equipmentLog?.pageInfo?.startCursor,
          response?.data?.equipmentLog?.pageInfo?.endCursor
        )

        this.ELEMENT_DATA = [];
        this.isReady = false;
        this.length = this.paginationProperties?.total;

        // Adiciona cada log retornado pela requisição na lista
        response?.data?.equipmentLog?.edges.forEach((node: any) => {
          this.ELEMENT_DATA.push(new EquipmentLog(
            node?.node?.username,
            node?.node?.operation,
            node?.node?.logDatetime,
            node?.node?.serialNumber,
            node?.node?.macAddress,
            node?.node?.company?.name
          ));
        });

        this.setDataSourceAttributes();

        this.filterLoading = false;

        if (this.ELEMENT_DATA.length === 0) {
          // Retorna um aviso de erro ao usuário
          Swal.fire({
            title: 'Não Encontrado',
            text: 'Não há registros encontrados para esta busca.',
            icon: 'error',
            confirmButtonText: 'Ok'
          });
        }
      })
    });;
  }

  // Método para obter a chave de tradução correta com base em 'element.operation'
  public getTranslationKey(element: any): string {
    return `logs.form-${element.operation}-label`;
  }

  // ---------------------------------------------------------------------
  // Código destinado à funções de download de CSV e PDF da página.

  public generateFileData(cursor: string | null, fileType: string) {
    this.fileLoading = true;
    /** Verifica se a lista está pronta **/
    if (this.isReady == false) {
      this.subscriptions = this.equipmentLogService.filterLogs(
        this.companyId,
        this.operation,
        this.startDatetime,
        this.endDatetime,
        this.serialNumber,
        this.macAddress,
        this.username,
        cursor,
        100
      ).valueChanges.subscribe({
        next: ((response: any) => {
          /** Valida se a resposta está carregando ou não possui dados **/
          if (response.loading || !response.data) {
            return;
          }

          response?.data?.equipmentLog?.edges.forEach((node: any) => {

            /** Tratamento de dados retornados da API **/
            this.FILE_DATA.push(
              new EquipmentLog(
                node?.node?.username,
                node?.node?.operation,
                node?.node?.logDatetime,
                node?.node?.serialNumber,
                node?.node?.macAddress,
                node?.node?.company?.name
              )
            );
          })

          /** Caso seja a última página a lista está completa **/
          if (!response?.data?.equipmentLog?.pageInfo?.hasNextPage) {
            this.isReady = true;
          }

          this.generateFileData(response?.data?.equipmentLog?.pageInfo?.endCursor, fileType);
        }),
        error: ((error: any) => {
          this.errorLibService.errorAlert(error);
        })
      })
    }
    /** Caso a lista esteja pronta gera o arquivo escolhido pelo usuário **/
    else {
      this.fileLoading = false;
      if (fileType === "PDF") {
        this.downloadPDF();
      } else {
        this.downloadCSV();
      }
    }
  }

  // Converte o conteúdo em CSV. Privado porque só usa aqui nessa classe.
  private convertToCSV(arr: any) {
    // Transforma o objeto numa lista onde o primeiro elemento é o cabeçalho com o nome das colunas.
    const content = [Object.keys(arr[0])].concat(arr);

    // Retorna o conteúdo mapeado como string linha a linha adicionando quebra de linha em cada um.
    return content
      .map((item) => {
        return Object.values(item).toString();
      })
      .join('\n');
  }

  // Função que realiza o download do CSV.
  public downloadCSV() {
    let content: any = [];

    // Adicoina cada elemento como um objeto na lista de conteúdo.
    this.FILE_DATA.forEach((log) => {
      content.push({
        Username: log?.username,
        Operation: log?.operation,
        LogDatetime: tzConvertUTC2Local(log?.logDatetime),
        SerialNumber: log?.serialNumber,
        MacAddress: log?.macAddress,
        Company: log?.company
      });
    });

    // Adiciona o conteúdo convertido dentro de uma nova variável.
    let data = this.convertToCSV(content);
    // Transforma o conteúdo para o formato blob para geração do arquivo.
    let file = new Blob([data], { type: 'text/csv' });
    // Cria um endereçamento para o arquivo.
    let url = window.URL.createObjectURL(file);
    // Cria um elemento para a linkagem do arquivo.
    let link = document.createElement('a');
    // Adiciona a url no elemento de linkagem.
    link.href = url;
    // Nomeia o arquivo que será feito o download.
    link.download = `${new Date().toLocaleDateString()}-${new Date().getHours()}${new Date().getMinutes()}${new Date().getSeconds()}-equipment-log.csv`;
    // Adiciona o link no documento.
    document.body.appendChild(link);
    // Atica o evento de click para realizar o download.
    link.click();
    // Remove o link do documento.
    document.body.removeChild(link);
    // Remove o endereço do download.
    window.URL.revokeObjectURL(url);

    this.FILE_DATA = [];
    this.isReady = false;
  }

  // Função que realiza o download do CSV.
  public downloadPDF() {
    // Define o cabeçalho do documento.
    const header = ['Username', 'Operation', 'LogDatetime', 'SerialNumber', 'MacAddress', 'Company'];
    // Lista que irá conter o conteúdo retornado pela API.
    let content: any = [];
    // Inserção do conteúdo na lista.
    this.FILE_DATA.forEach((log) => {
      content.push([
        log?.username,
        log?.operation,
        tzConvertUTC2Local(log?.logDatetime),
        log?.serialNumber,
        log?.macAddress,
        log?.company
      ]);
    });
    // Define o formato do documento.
    let file: any = new jsPDF('l', 'mm', 'a4');
    // Cria o PDF.
    file.autoTable({
      theme: 'grid',
      margin: { top: 20 },
      head: [header],
      body: content,
    });
    // Gera o download do documento.
    file.save(
      `${new Date().toLocaleDateString()}-${new Date().getHours()}:${new Date().getMinutes()}-equipment-log.pdf`
    );

    this.FILE_DATA = [];
    this.isReady = false;
  }

  /** Realiza a conversão da data p/ fuso horário do navegador ou fuso horário especificado pelo usuário 
 * (Utilizado na data exibida na tabela de logs) **/
  public formatDate(date: string): any {
    return tzConvertUTC2Local(date)
  }
}
